import React, { useState, useEffect, Fragment } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import clsx from "clsx";

// Material-UI Core
import { makeStyles } from "@material-ui/core/styles";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import Box from "@material-ui/core/Box";
import InputBase from "@material-ui/core/InputBase";
import ButtonBase from "@material-ui/core/ButtonBase";

// Material-UI Icon
import DownloadIcon from "@material-ui/icons/GetApp";
import CirculateIcon from "@material-ui/icons/Mail";
import PasteIcon from "@material-ui/icons/Assignment";
import MoveIcon from "@material-ui/icons/OpenWith";
import NewFolderIcon from "@material-ui/icons/CreateNewFolder";
import DeleteIcon from "@material-ui/icons/Delete";
import AddDocumentIcon from "@material-ui/icons/NoteAdd";
import SearchIcon from "@material-ui/icons/Search";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import CancelIcon from "@material-ui/icons/Cancel";

// Custom Style
import fileManagerToolbarStyle from "../../styles/jss/components/fileManager/fileManagerToolbarStyle";

// Custom Component
import LockScreenLoading from "../LockScreenLoading/LockScreenLoading";
import { Alert } from "../Alert/Alert";
import FullScreenDialog from "../Dialog/FullScreenDialog";
import NormalDialog from "../Dialog/NormalDialog";
import createFileChecksum from "../../global/createFileChecksum";
import UploadFileForm from "./UploadFileForm";
import CirculationCreateForm from "../Circulation/CirculationCreateForm";
import ScanFileList from "./ScanFileList";

// Global Function
import { getFile, getSubject } from "../../global/getPropertyInfo";

// Fetch Data
import { fileUpload, createFileProperty } from "../../dataSource/fileManager";

const useStyles = makeStyles((theme) => fileManagerToolbarStyle(theme));

const FileManagerToolbar = (props) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const {
    fileManagerType,
    folderRight,
    numSelected,
    propertySelected,
    createFolderDialogHandleOpen,
    confirmDialogHandleOpen,
    confirmDialogHandleClose,
    removeFolderAction,
    removeFileAction,
    propertyDownloadAction,
    resetMovePropertyControl,
    setResetMovePropertyControl,
    movePropertyAction,
    setQuickSearchKeyWord,
    advancedSearchDialogHandleOpen,
    setFileUploaded,
    setResetFileSelect,
  } = props;

  // Redux Data
  const folderSelectedResult = useSelector(
    (state) => state.folderSelectedRedux
  );
  const uploadFilePropertyRedux = useSelector(
    (state) => state.uploadFilePropertyRedux
  );
  const propertySeqRedux = useSelector((state) => state.propertySeqRedux);

  // Lock Screen Loading
  const [lockScreenProgressTitle, setLockScreenProgressTitle] = useState("");
  const [lockScreenLoadingOpen, setLockScreenLoadingOpen] = useState(false);

  const [addFileDialogOpen, setAddFileDialogOpen] = useState(false);
  const [newCirculationDialogOpen, setNewCirculationDialogOpen] =
    useState(false);
  const [movePropertyControl, setMovePropertyControl] = useState(false);
  const [selectFileOpen, setSelectFileOpen] = useState();
  const [acceptedFilesPool, setAcceptedFilesPool] = useState([]);
  const [propertyFiles, setPropertyFiles] = useState(null);
  const [propertySubject, setPropertySubject] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [scanValidationErrors, setScanValidationErrors] = useState([]);

  const [selection, setSelection] = useState([]);
  const [scanfileList, setScanFileList] = useState([]);
  const [filteredScanfileList, setFilteredScanFileList] = useState([]);

  const [scanDialog, setScanDialog] = useState(false);

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertData, setAlertData] = useState({
    type: "",
    title: "",
    content: "",
  });

  // Build Alert
  const buildAlert = (data) => {
    setAlertData({
      type: data.type,
      title: data.title,
      content: data.content,
    });

    alertOpenHandle();
  };
  // Handle Alert open and close
  const alertOpenHandle = () => {
    setAlertOpen(true);
  };

  const alertCloseHandle = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setAlertOpen(false);
  };

  // Dialog open and close
  const uploadFileDialogHandleOpen = () => {
    if (folderSelectedResult === null) {
      buildAlert({
        // Alert Type: success, info, warning, error
        type: "warning",
        title: "warning Alert",
        content: "No folder specify. Please select the folder.",
      });
      return;
    }

    if (!folderSelectedResult.rights.create) {
      buildAlert({
        // Alert Type: success, info, warning, error
        type: "warning",
        title: "warning Alert",
        content: "No folder rights to upload document.",
      });
      return;
    }

    setAddFileDialogOpen(true);
  };

  const uploadFileDialogHandleClose = () => {
    setAddFileDialogOpen(false);
    setFilteredScanFileList([]);
    setSelection([]);
    setScanValidationErrors([])
    setValidationErrors([])
    resetFileUploadStorage();
  };

  const newCirculationDialogHandleOpen = () => {
    setNewCirculationDialogOpen(true);
  };

  const newCirculationDialogHandleClose = () => {
    resetCirculationData();
    setNewCirculationDialogOpen(false);
  };

  const pastePropertyOpen = () => {
    if (propertySeqRedux === null) {
      buildAlert({
        // Alert Type: success, info, warning, error
        type: "warning",
        title: "warning Alert",
        content: "No file selected. Please select file.",
      });
      return;
    }

    setMovePropertyControl(true);

    buildAlert({
      // Alert Type: success, info, warning, error
      type: "info",
      title: "Information Alert",
      content: "Please choose the destination folder then paste the file",
    });
  };

  const pastePropertyClose = () => {
    setMovePropertyControl(false);
  };

  // Confirm Dialog
  const removeFolderConfirmAction = () => {
    confirmDialogHandleClose();
    removeFolderAction();
  };

  const removeFolderCancelAction = () => {
    confirmDialogHandleClose();
  };

  const confirmDialogDeleteFolder = () => {
    if (folderSelectedResult === null) {
      buildAlert({
        // Alert Type: success, info, warning, error
        type: "warning",
        title: "warning Alert",
        content: "No folder specify. Please select the folder.",
      });
      return;
    }

    if (folderSelectedResult.folder_type !== "CUSTOM") {
      buildAlert({
        // Alert Type: success, info, warning, error
        type: "warning",
        title: "warning Alert",
        content: `${folderSelectedResult.folder_type} folder can't delete`,
      });
      return;
    }

    if (!folderSelectedResult.rights.delete) {
      buildAlert({
        // Alert Type: success, info, warning, error
        type: "warning",
        title: "warning Alert",
        content: "No folder rights to remove folder.",
      });
      return;
    }

    confirmDialogHandleOpen({
      dialogTitle: "Remove Folder",
      dialogContent: "Do you want to remove the folder.",
      confirmAction: removeFolderConfirmAction,
      cancelAction: removeFolderCancelAction,
    });
  };

  const confirmDialogDeleteFile = () => {
    if (propertySeqRedux.length <= 0) {
      buildAlert({
        // Alert Type: success, info, warning, error
        type: "warning",
        title: "warning Alert",
        content: "No file to remvoe. Please select file",
      });
    }

    confirmDialogHandleOpen({
      dialogTitle: "Remove File",
      dialogContent: "Do you want to remove the file.",
      confirmAction: removeFileConfirmAction,
      cancelAction: removeFileCancelAction,
    });
  };

  const removeFileConfirmAction = () => {
    removeFileAction();
    confirmDialogHandleClose();
  };

  const removeFileCancelAction = () => {
    confirmDialogHandleClose();
  };

  const selectScanOpen = () => {
    setScanDialog(true);
  };

  const selectScanClose = () => {
    setScanDialog(false);
  };

  const removeUploadedFileRecord = async (file) => {
    const index = acceptedFilesPool.indexOf(file);

    if (index > -1) {
      acceptedFilesPool.splice(index, 1);
      setAcceptedFilesPool(acceptedFilesPool);

      uploadFilePropertyRedux.splice(index, 1);
      dispatch({
        type: "UPLOAD_FILE_PROPERTY_ADD",
        data: uploadFilePropertyRedux,
      });
    }
  };

  // Main Action
  const fileUploadAction = async () => {
    if (acceptedFilesPool.length === 0 && filteredScanfileList.length === 0) {
      buildAlert({
        // Alert Type: success, info, warning, error
        type: "warning",
        title: "Warning Alert",
        content: "No file to upload. Please choose the file first.",
      });

      return;
    }

    if (validate()) {
      setLockScreenProgressTitle("Uploading...");
      setLockScreenLoadingOpen(true);

      await Promise.all(
        filteredScanfileList.map(async (file) => {
          try {
            await createFileProperty([file]).then(async (res) => {
              await removeUploadedFileRecord(file);

              buildAlert({
                // Alert Type: success, info, warning, error
                type: "success",
                title: "Success Alert",
                content: `${res.data[0].filename} => upload successful.`,
              });
            });
          } catch (err) {
            console.log(err);
          }
        }),
        acceptedFilesPool.map(async (file) => {
          const checksum = await createFileChecksum(file);

          try {
            await fileUpload(file, checksum).then(async (uploadRes) => {
              setFileUploaded(Math.random());
              await Promise.all(
                uploadFilePropertyRedux.map(async (fileProps) => {
                  if (
                    fileProps.filename.indexOf(uploadRes.data[0].filename) > -1
                  ) {
                    fileProps.file_seq = uploadRes.data[0].file_seq;

                    await createFileProperty([fileProps]).then(async (res) => {
                      await removeUploadedFileRecord(file);

                      buildAlert({
                        // Alert Type: success, info, warning, error
                        type: "success",
                        title: "Success Alert",
                        content: `${res.data[0].filename} => upload successful.`,
                      });
                    });
                  }
                })
              );
            });
          } catch (error) {
            buildAlert({
              // Alert Type: success, info, warning, error
              type: "error",
              title: "Error Alert",
              content: `${error}`,
            });
          }
        })
      ).then((res) => {
        setSelection([]);
        setLockScreenLoadingOpen(false);
        uploadFileDialogHandleClose();

        setFileUploaded(Math.random());
      });

      //   if (acceptedFilesPool.length === 0) {
      //     uploadFileDialogHandleClose();
      //   } else {
      //     buildAlert({
      //       // Alert Type: success, info, warning, error
      //       type: "warning",
      //       title: "Warning Alert",
      //       content: "Some file can't upload, you may press UPLOAD to try again.",
      //     });
      //   }
    }
  };

  const quickSearch = (event) => {
    const keyWord = event.target.value !== null ? event.target.value : "";
    setQuickSearchKeyWord(keyWord);
  };

  const sendCirculation = async () => {
    setPropertyFiles(await getFile(propertySelected));
    // setPropertySubject(await getSubject(propertySelected))

    newCirculationDialogHandleOpen();
  };

  const resetCirculationData = () => {
    dispatch({
      type: "CREATE_CIRCULATION_DATA",
      data: null,
    });
  };

  // Reset Storage
  const resetFileUploadStorage = () => {
    setAcceptedFilesPool([]);

    dispatch({
      type: "UPLOAD_FILE_PROPERTY_ADD",
      data: null,
    });
  };

  const getScanFile = () => {
    setScanDialog(true);
  };

  const uploadFileDialogBtn = [
    {
      buttonName: "Get Scan Files",
      buttonFunction: getScanFile,
      chooseFile: false,
    },
    {
      buttonName: "Upload",
      buttonFunction: fileUploadAction,
      chooseFile: false,
    },

    {
      buttonName: "Select File",
      buttonFunction: selectFileOpen,
      chooseFile: false,
    },
  ];

  const validate = (
    fieldValues = uploadFilePropertyRedux,
    scanFields = filteredScanfileList
  ) => {
    let temp = [...validationErrors];
    let scanTemp = [...scanValidationErrors];

    if (fieldValues !== null) {
      fieldValues.map((res, index) => {
        const tempFileProperty = {
          company_seq: res.company_seq !== 0 ? "" : "Required",
        };

        temp[index] = tempFileProperty;
        setValidationErrors([...temp]);
      });
    }

    scanFields.map((res, index) => {
      const tempFileProperty = {
        company_seq: res.company_seq !== 0 ? "" : "Required",
      };

      scanTemp[index] = tempFileProperty;
      setScanValidationErrors([...scanTemp]);
    });

    let check = true;
    [...temp, ...scanTemp].map((res) => {
      if (!Object.values(res).every((x) => x === "")) {
        check = false;
      }
    });

    return check;
  };

  useEffect(() => {
    if (resetMovePropertyControl) {
      setMovePropertyControl(false);
      setResetMovePropertyControl(false);
    }
  }, [resetMovePropertyControl]);

  return (
    // console.log("acceptedFilesPool | Variable: acceptedFilesPool (FileManagerToolbar.js)"),
    // console.log(acceptedFilesPool),

    // console.log("Folder Right | Variable: folderRight (FileManagerToolbar.js)"),
    // console.log(folderRight),

    // console.log("Property Seqance | Variable: propertySelected (FileManagerToolbar.js)"),
    // console.log(propertySelected),

    // console.log("Selected Property Files | Variable: propertyFiles (FileManagerToolbar.js)"),
    // console.log(propertyFiles),

    // console.log("Upload File Property (Redux) | Variable: uploadFilePropertyRedux (FileManagerToolbar.js)"),
    // console.log(uploadFilePropertyRedux),

    // console.log("Property Subject | Variable: propertySubject (FileManagerToolbar.js)"),
    // console.log(propertySubject),

    // console.log("---------------------------------------------------------------------------------"),

    <Fragment>
      <div>
        <FullScreenDialog
          open={newCirculationDialogOpen}
          dialogTitle={"New Circulation"}
          formContent={
            <CirculationCreateForm
              // presetSubject={propertySubject}
              setLockScreenLoadingOpen={setLockScreenLoadingOpen}
              presetFiles={propertyFiles}
              setResetFileSelect={setResetFileSelect}
              setFileUploaded={setFileUploaded}
              closeCirculationDialog={newCirculationDialogHandleClose}
              renewCircList={null}
              setRenewCircList={null}
              buildAlert={buildAlert}
            />
          }
          button={[]}
          handleClose={newCirculationDialogHandleClose}
        />

        <FullScreenDialog
          open={addFileDialogOpen}
          dialogTitle={"Upload Document"}
          formContent={
            <UploadFileForm
              selectFileOpen={setSelectFileOpen}
              acceptedFilesPool={acceptedFilesPool}
              setAcceptedFilesPool={setAcceptedFilesPool}
              setFilteredScanFileList={setFilteredScanFileList}
              filteredScanfileList={filteredScanfileList}
              lockScreenProgressTitle={lockScreenProgressTitle}
              lockScreenLoadingOpen={lockScreenLoadingOpen}
              selection={selection}
              setSelection={setSelection}
              validate={validate}
              scanValidationErrors={scanValidationErrors}
              setScanValidationErrors={setScanValidationErrors}
              validationErrors={validationErrors}
              setValidationErrors={setValidationErrors}
            />
          }
          button={uploadFileDialogBtn}
          handleClose={uploadFileDialogHandleClose}
        />

        <NormalDialog
          open={scanDialog}
          dialogTitle={`Select Scan Files`}
          formContent={
            selection && (
              <ScanFileList
                scanfileList={scanfileList}
                setScanFileList={setScanFileList}
                filteredScanfileList={filteredScanfileList}
                setFilteredScanFileList={setFilteredScanFileList}
                acceptedFilesPool={acceptedFilesPool}
                setAcceptedFilesPool={setAcceptedFilesPool}
                selectScanClose={selectScanClose}
                selection={selection}
                setSelection={setSelection}
                scanValidationErrors={scanValidationErrors}
                setScanValidationErrors={setScanValidationErrors}
              />
            )
          }
          size={"lg"}
          height={780}
          button={[]}
          handleClose={selectScanClose}
        />

        <Alert
          open={alertOpen}
          alertType={alertData.type}
          alertTitle={alertData.title}
          alertContent={alertData.content}
          handleClose={alertCloseHandle}
        />
      </div>

      <Toolbar
        className={clsx(classes.root, {
          [classes.highlight]: numSelected > 0,
        })}
      >
        {numSelected > 0 ? (
          <Typography
            className={classes.title}
            color="inherit"
            variant="subtitle1"
            component="div"
          >
            {numSelected} SELECTED
          </Typography>
        ) : (
          <div className={classes.title}></div>
        )}

        {numSelected > 0 ? (
          movePropertyControl ? (
            <Box>
              <Tooltip title="Paste">
                <IconButton aria-label="paste" onClick={movePropertyAction}>
                  <PasteIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Cancel Paste">
                <IconButton
                  aria-label="cancel paste"
                  onClick={pastePropertyClose}
                >
                  <CancelIcon />
                </IconButton>
              </Tooltip>
            </Box>
          ) : (
            <Box>
              {folderRight ? (
                folderRight.workflow ? (
                  fileManagerType === "main" ? (
                    <Tooltip title="Circulate">
                      <IconButton
                        aria-label="circulate"
                        onClick={sendCirculation}
                      >
                        <CirculateIcon />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    ""
                  )
                ) : (
                  ""
                )
              ) : (
                ""
              )}

              <Tooltip title="Download">
                <IconButton
                  aria-label="download"
                  onClick={propertyDownloadAction}
                >
                  <DownloadIcon />
                </IconButton>
              </Tooltip>

              {folderRight ? (
                folderRight.move ? (
                  <Tooltip title="Move File">
                    <IconButton aria-label="move" onClick={pastePropertyOpen}>
                      <MoveIcon />
                    </IconButton>
                  </Tooltip>
                ) : (
                  ""
                )
              ) : (
                ""
              )}

              {folderRight ? (
                folderRight.delete ? (
                  <Tooltip title="Delete File">
                    <IconButton
                      aria-label="delete"
                      onClick={confirmDialogDeleteFile}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                ) : (
                  ""
                )
              ) : (
                ""
              )}
            </Box>
          )
        ) : (
          <Box className={classes.toolBar}>
            <div className={classes.searchBar}>
              <div className={classes.searchIcon}>
                <SearchIcon />
              </div>

              <InputBase
                onChange={quickSearch}
                placeholder="Quick Search"
                classes={{
                  root: classes.inputRoot,
                  input: classes.inputInput,
                }}
                inputProps={{ "aria-label": "search" }}
              />

              <div className={classes.downIcon}>
                <Tooltip title="Advanced Search">
                  <ButtonBase
                    aria-label="search"
                    onClick={advancedSearchDialogHandleOpen}
                  >
                    <ArrowDropDownIcon />
                  </ButtonBase>
                </Tooltip>
              </div>
            </div>

            {folderRight ? (
              folderRight.create ? (
                <Box>
                  <Tooltip title="New Folder">
                    <IconButton
                      aria-label="newFolder"
                      onClick={createFolderDialogHandleOpen}
                    >
                      <NewFolderIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              ) : (
                ""
              )
            ) : (
              ""
            )}

            {folderRight ? (
              folderRight.delete ? (
                <Box>
                  <Tooltip title="Delete Folder">
                    <IconButton
                      aria-label="delFolder"
                      onClick={confirmDialogDeleteFolder}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              ) : (
                ""
              )
            ) : (
              ""
            )}

            {folderRight ? (
              folderRight.write ? (
                <Box>
                  <Tooltip title="Add Document">
                    <IconButton
                      aria-label="addDocument"
                      onClick={uploadFileDialogHandleOpen}
                    >
                      <AddDocumentIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              ) : (
                ""
              )
            ) : (
              ""
            )}
          </Box>
        )}
      </Toolbar>
    </Fragment>
  );
};

FileManagerToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
};

export default FileManagerToolbar;
